import {Component, DestroyRef, ElementRef, effect, inject, signal, ViewChild} from '@angular/core';
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {SidebarService} from "../../../core/services/sidebar.service";
import {DatePipe, NgOptimizedImage, NgTemplateOutlet} from "@angular/common";
import {MatRippleModule} from "@angular/material/core";
import {AuthService} from "../../../core/api-services/auth.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {MatDivider} from "@angular/material/divider";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {Router, RouterLink} from "@angular/router";
import {environment} from "../../../../environments/environment";
import {ScreenSizeService} from "../../../core/services/screen-size.service";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, switchMap, timer} from 'rxjs';
import {NotificationService, UserNotification} from '../../../core/api-services/notification.service';
import {notificationDescriptionLines} from '../../utils/notification-description';
import {MobileEntityHeaderService} from '../../../core/services/mobile-entity-header.service';
import {SystemSettingsService} from '../../../core/services/system-settings.service';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {DashboardPreviewService} from '../../../core/services/dashboard-preview.service';
import {SwitchDashboardDialogComponent} from '../switch-dashboard-dialog/switch-dashboard-dialog.component';
import {CrmControlBannerComponent} from '../crm-control-banner/crm-control-banner.component';
import {AdminSessionService} from '../../../core/services/admin-session.service';

@Component({
  selector: 'app-header',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    NgOptimizedImage,
    DatePipe,
    MatRippleModule,
    MatDivider,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    RouterLink,
    NgTemplateOutlet,
    MatDialogModule,
    CrmControlBannerComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  readonly adminSession = inject(AdminSessionService);
  readonly helpUrl = inject(SystemSettingsService).helpUrl;
  readonly dashboardPreview = inject(DashboardPreviewService);
  readonly mobileEntityHeader = inject(MobileEntityHeaderService);
  private readonly dialog = inject(MatDialog);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private authService = inject(AuthService);
  private sidebarService = inject(SidebarService);
  private screenService = inject(ScreenSizeService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  @ViewChild('notificationMenuTrigger') private notificationMenuTrigger?: MatMenuTrigger;

  user = toSignal(this.authService.currentUser$, {initialValue: null})
  mobileMode = this.screenService.isMobile;
  isProduction = environment.production;
  notifications = signal<UserNotification[]>([]);
  unreadCount = signal(0);
  readonly notificationDescriptionLines = notificationDescriptionLines;

  constructor() {
    effect(onCleanup => {
      if (!this.mobileEntityHeader.template()) return;
      const frame = requestAnimationFrame(() => {
        this.element.nativeElement.querySelector<HTMLElement>('.mobile-record-tabs .active')
          ?.scrollIntoView({block: 'nearest', inline: 'nearest'});
      });
      onCleanup(() => cancelAnimationFrame(frame));
    });
    timer(0, 60_000)
      .pipe(
        switchMap(() => this.notificationService.list({prioritizeUnread: true}).pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(response => {
        this.notifications.set(response.data.notifications);
        this.unreadCount.set(response.data.unread_count);
      });
  }

  public toggleSideNav() {
    this.sidebarService.toggle();
  }

  logout() {
    this.authService.logout();
  }

  switchDashboard(): void {
    if (!this.dashboardPreview.allowed()) return;
    this.dialog.open(SwitchDashboardDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: 'dialog',
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userId: string | undefined) => {
      if (typeof userId === 'string' && this.dashboardPreview.allowed()) {
        this.dashboardPreview.selectUser(userId);
      }
    });
  }

  markRead(notification: UserNotification): void {
    if (notification.read_at !== null) {
      return;
    }

    this.notificationService.markRead(notification.id).subscribe(response => {
      const updated = response.data.notification;
      this.updateReadNotification(notification, updated);
    });
  }

  openNotification(notification: UserNotification): void {
    this.notificationMenuTrigger?.closeMenu();

    const navigateToEntity = () => {
      if (notification.entity && notification.entity_id !== null) {
        void this.router.navigate(['/dashboard', 'form', notification.entity, notification.entity_id]);
      }
    };

    if (notification.read_at !== null) {
      navigateToEntity();
      return;
    }

    this.notificationService.markRead(notification.id).subscribe({
      next: response => {
        const updated = response.data.notification;
        this.updateReadNotification(notification, updated);
        navigateToEntity();
      },
      error: navigateToEntity,
    });
  }

  private updateReadNotification(previous: UserNotification, updated: UserNotification): void {
    this.notifications.update(items => items.map(item => item.id === updated.id ? updated : item));

    if (previous.read_at === null && updated.read_at !== null) {
      this.unreadCount.update(count => Math.max(0, count - 1));
    }
  }

  viewAllNotifications(): void {
    this.notificationMenuTrigger?.closeMenu();
    void this.router.navigate(['/dashboard', 'notifications']);
  }
}
